import React, { useState, useEffect } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import { 
  Add, 
  Save, 
  Cancel, 
  Edit, 
  Delete, 
  Visibility,
  Close,
  Search
} from "@mui/icons-material";
import axios from "axios";

const EmployeeDirectory = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    employeeId: "",
    name: "",
    department: "",
    designation: "",
    grade: "",
    manager: "",
    email: "",
    phoneNumber: "",
    joiningDate: "",
    location: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Sample departments
  const departments = [
    "IT",
    "Human Resources",
    "Finance",
    "Marketing",
    "Sales",
    "Operations",
    "Research & Development",
    "Customer Support"
  ];

  // Sample designations
  const designations = [
    "Software Engineer",
    "Senior Software Engineer",
    "Team Lead",
    "Manager",
    "Director",
    "VP",
    "CEO",
    "HR Executive",
    "Finance Analyst",
    "Sales Executive"
  ];

  // Sample grades
  const grades = ["A1", "A2", "B1", "B2", "C1", "C2", "D1", "D2"];

  const handleNewEmployeeChange = (field, value) => {
    setNewEmployee(prev => ({ ...prev, [field]: value }));
  };

  const handleAddNew = async () => {
    if (newEmployee.employeeId && newEmployee.name && newEmployee.department) {
      try {
        // Replace with your actual API endpoint
        const response = await axios.post("http://10.10.222.56:8283/employees/AddEmployee", newEmployee);
        
        if (response.data.success) {
          setEmployees([{ ...newEmployee, id: Date.now() }, ...employees]);
          resetNewEmployee();
          setShowAddRow(false);
          alert("Employee added successfully!");
        } else {
          console.error("Failed to add employee:", response.data.message);
          alert("Failed to add employee. Please try again.");
        }
      } catch (error) {
        console.error("Error adding employee:", error);
        // For demo, add locally
        setEmployees([{ ...newEmployee, id: Date.now() }, ...employees]);
        resetNewEmployee();
        setShowAddRow(false);
        alert("Employee added successfully! (Demo Mode)");
      }
    } else {
      alert("Employee ID, Name, and Department are required fields!");
    }
  };

  const resetNewEmployee = () => {
    setNewEmployee({
      employeeId: "",
      name: "",
      department: "",
      designation: "",
      grade: "",
      manager: "",
      email: "",
      phoneNumber: "",
      joiningDate: "",
      location: ""
    });
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleEmployeeChange = (id, field, value) => {
    setEmployees(prev =>
      prev.map(employee =>
        employee.id === id ? { ...employee, [field]: value } : employee
      )
    );
  };

  const handleSave = async (id) => {
    const employeeToUpdate = employees.find(employee => employee.id === id);
    if (employeeToUpdate) {
      try {
        // Replace with your actual API endpoint
        const response = await axios.put(`http://10.10.222.56:8283/employees/UpdateEmployee/${id}`, employeeToUpdate);
        
        if (response.data.success) {
          setEditingId(null);
          alert("Employee updated successfully!");
        } else {
          console.error("Failed to update employee:", response.data.message);
          alert("Failed to update employee.");
        }
      } catch (error) {
        console.error("Error updating employee:", error);
        // For demo, update locally
        setEditingId(null);
        alert("Employee updated successfully! (Demo Mode)");
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        // Replace with your actual API endpoint
        const response = await axios.delete(`http://10.10.222.56:8283/employees/DeleteEmployee/${id}`);
        
        if (response.data.success) {
          setEmployees(prev => prev.filter(employee => employee.id !== id));
          alert("Employee deleted successfully!");
        } else {
          console.error("Failed to delete employee:", response.data.message);
          alert("Failed to delete employee.");
        }
      } catch (error) {
        console.error("Error deleting employee:", error);
        // For demo, delete locally
        setEmployees(prev => prev.filter(employee => employee.id !== id));
        alert("Employee deleted successfully! (Demo Mode)");
      }
    }
  };

  const handleView = (employee) => {
    setSelectedEmployee(employee);
    setViewDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    
    let filtered = employees;
    
    if (term) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(term) ||
        emp.employeeId.toLowerCase().includes(term) ||
        emp.department.toLowerCase().includes(term) ||
        emp.designation.toLowerCase().includes(term)
      );
    }
    
    if (filterDepartment !== "all") {
      filtered = filtered.filter(emp => emp.department === filterDepartment);
    }
    
    setFilteredEmployees(filtered);
  };

  const handleFilterChange = (event) => {
    const dept = event.target.value;
    setFilterDepartment(dept);
    
    let filtered = employees;
    
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchTerm) ||
        emp.employeeId.toLowerCase().includes(searchTerm) ||
        emp.department.toLowerCase().includes(searchTerm) ||
        emp.designation.toLowerCase().includes(searchTerm)
      );
    }
    
    if (dept !== "all") {
      filtered = filtered.filter(emp => emp.department === dept);
    }
    
    setFilteredEmployees(filtered);
  };

  useEffect(() => {
    // Fetch employees data
    const fetchEmployees = async () => {
      try {
        // Replace with your actual API endpoint
        const response = await axios.get("http://10.10.222.56:8283/employees/GetAllEmployees");
        if (response.data.success) {
          setEmployees(response.data.data);
          setFilteredEmployees(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        // For demo, use sample data
        const sampleEmployees = [
          {
            id: 1,
            employeeId: "EMP001",
            name: "John Doe",
            department: "IT",
            designation: "Software Engineer",
            grade: "A1",
            manager: "Jane Smith",
            email: "john.doe@company.com",
            phoneNumber: "9876543210",
            joiningDate: "2023-01-15",
            location: "New York"
          },
          {
            id: 2,
            employeeId: "EMP002",
            name: "Jane Smith",
            department: "IT",
            designation: "Team Lead",
            grade: "B1",
            manager: "Robert Johnson",
            email: "jane.smith@company.com",
            phoneNumber: "9876543211",
            joiningDate: "2022-06-01",
            location: "California"
          },
          {
            id: 3,
            employeeId: "EMP003",
            name: "Alice Johnson",
            department: "HR",
            designation: "HR Manager",
            grade: "B2",
            manager: "Michael Brown",
            email: "alice.johnson@company.com",
            phoneNumber: "9876543212",
            joiningDate: "2021-03-10",
            location: "Texas"
          }
        ];
        setEmployees(sampleEmployees);
        setFilteredEmployees(sampleEmployees);
        alert("Using demo data. Please configure your API endpoints.");
      }
    };
    
    fetchEmployees();
  }, []);

  // Responsive table header
  const tableHeaders = isMobile ? 
    ["S.NO", "Emp ID", "Name", "Department", "Actions"] :
    ["S.NO", "Employee ID", "Employee Name", "Department", "Designation", "Grade", "Employee Manager", "Actions"];

  // Mobile View - Card layout
  if (isMobile) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Employee Directory
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setShowAddRow(true)}
            size="small"
          >
            Add Employee
          </Button>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name, ID, department..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
            }}
            sx={{ mb: 1 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Department</InputLabel>
            <Select
              value={filterDepartment}
              label="Filter by Department"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">All Departments</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {showAddRow && (
          <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Add New Employee
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Employee ID"
                    size="small"
                    value={newEmployee.employeeId}
                    onChange={(e) => handleNewEmployeeChange("employeeId", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    size="small"
                    value={newEmployee.name}
                    onChange={(e) => handleNewEmployeeChange("name", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={newEmployee.department}
                      label="Department"
                      onChange={(e) => handleNewEmployeeChange("department", e.target.value)}
                      required
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Designation"
                    size="small"
                    value={newEmployee.designation}
                    onChange={(e) => handleNewEmployeeChange("designation", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Grade"
                    size="small"
                    value={newEmployee.grade}
                    onChange={(e) => handleNewEmployeeChange("grade", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Manager"
                    size="small"
                    value={newEmployee.manager}
                    onChange={(e) => handleNewEmployeeChange("manager", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    size="small"
                    type="email"
                    value={newEmployee.email}
                    onChange={(e) => handleNewEmployeeChange("email", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button 
                      variant="contained" 
                      onClick={handleAddNew}
                      disabled={!newEmployee.employeeId || !newEmployee.name || !newEmployee.department}
                      size="small"
                      startIcon={<Save />}
                    >
                      Save
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setShowAddRow(false);
                        resetNewEmployee();
                      }}
                      size="small"
                      startIcon={<Close />}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((employee, index) => (
          <Card key={employee.id} sx={{ mb: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {employee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {employee.employeeId} | {employee.department}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {page * rowsPerPage + index + 1}
                </Typography>
              </Box>
              
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Designation:</strong> {employee.designation}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Grade:</strong> {employee.grade}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Manager:</strong> {employee.manager}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" noWrap>
                    <strong>Email:</strong> {employee.email}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                <IconButton size="small" onClick={() => handleView(employee)}>
                  <Visibility fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleEdit(employee.id)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(employee.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}

        <TablePagination
          component="div"
          count={filteredEmployees.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* View Dialog for Mobile */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Employee Details</DialogTitle>
          <DialogContent>
            {selectedEmployee && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography><strong>Employee ID:</strong> {selectedEmployee.employeeId}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Name:</strong> {selectedEmployee.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Department:</strong> {selectedEmployee.department}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Designation:</strong> {selectedEmployee.designation}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Grade:</strong> {selectedEmployee.grade}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Manager:</strong> {selectedEmployee.manager}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Email:</strong> {selectedEmployee.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Phone:</strong> {selectedEmployee.phoneNumber}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Joining Date:</strong> {selectedEmployee.joiningDate}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Location:</strong> {selectedEmployee.location}</Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Tablet and Desktop View
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography variant="h5" component="h1">
          Employee Directory
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => setShowAddRow(true)}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Add New Employee
        </Button>
      </Box>

      {/* Search and Filter */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search by name, ID, department, designation..."
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
          }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Department</InputLabel>
          <Select
            value={filterDepartment}
            label="Filter by Department"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size={isTablet ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      whiteSpace: 'nowrap',
                      padding: '8px'
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {showAddRow && (
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell align="center" sx={{ padding: '8px' }}>-</TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newEmployee.employeeId}
                      onChange={(e) => handleNewEmployeeChange("employeeId", e.target.value)}
                      required
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newEmployee.name}
                      onChange={(e) => handleNewEmployeeChange("name", e.target.value)}
                      required
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={newEmployee.department}
                        onChange={(e) => handleNewEmployeeChange("department", e.target.value)}
                        required
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={newEmployee.designation}
                          onChange={(e) => handleNewEmployeeChange("designation", e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={newEmployee.grade}
                          onChange={(e) => handleNewEmployeeChange("grade", e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={newEmployee.manager}
                          onChange={(e) => handleNewEmployeeChange("manager", e.target.value)}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton 
                        onClick={handleAddNew}
                        disabled={!newEmployee.employeeId || !newEmployee.name || !newEmployee.department}
                        color="primary"
                        size="small"
                      >
                        <Save />
                      </IconButton>
                      <IconButton 
                        onClick={() => {
                          setShowAddRow(false);
                          resetNewEmployee();
                        }}
                        color="error"
                        size="small"
                      >
                        <Cancel />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((employee, index) => (
                <TableRow key={employee.id}>
                  <TableCell align="center" sx={{ padding: '8px' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === employee.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={employee.employeeId}
                        onChange={(e) => handleEmployeeChange(employee.id, "employeeId", e.target.value)}
                      />
                    ) : (
                      employee.employeeId
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === employee.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={employee.name}
                        onChange={(e) => handleEmployeeChange(employee.id, "name", e.target.value)}
                      />
                    ) : (
                      employee.name
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === employee.id ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={employee.department}
                          onChange={(e) => handleEmployeeChange(employee.id, "department", e.target.value)}
                        >
                          {departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      employee.department
                    )}
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        {editingId === employee.id ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={employee.designation}
                            onChange={(e) => handleEmployeeChange(employee.id, "designation", e.target.value)}
                          />
                        ) : (
                          employee.designation
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        {editingId === employee.id ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={employee.grade}
                            onChange={(e) => handleEmployeeChange(employee.id, "grade", e.target.value)}
                          />
                        ) : (
                          employee.grade
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: '8px' }}>
                        {editingId === employee.id ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={employee.manager}
                            onChange={(e) => handleEmployeeChange(employee.id, "manager", e.target.value)}
                          />
                        ) : (
                          employee.manager
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {editingId === employee.id ? (
                        <>
                          <IconButton onClick={() => handleSave(employee.id)} size="small">
                            <Save color="primary" />
                          </IconButton>
                          <IconButton onClick={handleCancel} size="small">
                            <Cancel color="error" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={() => handleView(employee)} size="small">
                            <Visibility color="info" />
                          </IconButton>
                          <IconButton onClick={() => handleEdit(employee.id)} size="small">
                            <Edit color="primary" />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(employee.id)} size="small">
                            <Delete color="error" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredEmployees.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog for Tablet/Desktop */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Employee Details</DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Employee ID:</strong> {selectedEmployee.employeeId}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Name:</strong> {selectedEmployee.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Department:</strong> {selectedEmployee.department}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Designation:</strong> {selectedEmployee.designation}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Grade:</strong> {selectedEmployee.grade}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Manager:</strong> {selectedEmployee.manager}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Email:</strong> {selectedEmployee.email}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Phone:</strong> {selectedEmployee.phoneNumber}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Joining Date:</strong> {selectedEmployee.joiningDate}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Location:</strong> {selectedEmployee.location}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDirectory;